import { useEffect, useRef } from "react";

// Real-time reactive audio waveform driven by the live mic stream (Web Audio API).
// Falls back to a flat idle bar set when inactive or no stream.
export default function LiveWaveform({ stream, active, bars = 28, color = "bg-rose-500", className = "" }) {
  const refs = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!stream || !active) return;
    let ctx, analyser, src, data, stopped = false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.78;
      src = ctx.createMediaStreamSource(stream);
      src.connect(analyser);
      data = new Uint8Array(analyser.frequencyBinCount);
    } catch { return; }
    const n = refs.current.length;
    const draw = () => {
      if (stopped) return;
      analyser.getByteFrequencyData(data);
      for (let i = 0; i < n; i++) {
        // mirror from the centre so it reads like a classic dictaphone VU
        const dist = Math.abs(i - (n - 1) / 2) / ((n - 1) / 2);
        const bin = Math.floor(dist * (data.length * 0.65));
        const v = (data[bin] || 0) / 255;
        const h = Math.max(10, Math.min(100, v * 150 + 10));
        const el = refs.current[i];
        if (el) el.style.height = `${h}%`;
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      stopped = true;
      cancelAnimationFrame(rafRef.current);
      try { src.disconnect(); } catch {}
      try { ctx.close(); } catch {}
      refs.current.forEach((el) => { if (el) el.style.height = "12%"; });
    };
  }, [stream, active]);

  return (
    <div className={`flex items-center justify-center gap-1 h-12 ${className}`} data-testid="voice-waveform-live">
      {Array.from({ length: bars }).map((_, i) => (
        <div
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className={`w-1 rounded-full ${color} transition-[height] duration-75 ease-out`}
          style={{ height: active ? "16%" : "10%", opacity: active ? 1 : 0.3 }}
        />
      ))}
    </div>
  );
}
